import { useAuthSessionBounce } from "./useAuthSessionBounce";

export const SESSION_KEY = "external_auth_callback_port";

interface CallbackTarget {
	port: string;
	nonce: string | null;
	host: string;
}

/**
 * Handles the TVPN auth callback flow.
 *
 * When the app is opened with ?callback_port=<port>:
 * - If there's a session: redirects immediately with the existing token
 * - If no session: waits for sign-in, then redirects
 *
 * Returns true while the redirect is in progress (caller should show a loader).
 */
export function useExternalAuth(
	authServiceLoaded: boolean,
	hasSession: boolean | undefined,
	activeAuthId: string | undefined,
): boolean {
	return useAuthSessionBounce<CallbackTarget>(
		() => {
			const params = new URLSearchParams(window.location.search);
			const port = params.get("callback_port");
			if (!port) return null;

			sessionStorage.setItem(SESSION_KEY, port);

			return {
				port,
				nonce: params.get("nonce"),
				host: params.get("callback_host") ?? "localhost",
			};
		},
		false,
		authServiceLoaded,
		hasSession,
		activeAuthId,
		(tokenString, { port, nonce, host }) => {
			const [type, token] = tokenString.split(" ", 2);
			sessionStorage.removeItem(SESSION_KEY);
			const nonceParam = nonce ? `&nonce=${encodeURIComponent(nonce)}` : "";
			window.location.href = `http://${host}:${port}/api/auth/callback?access_token=${encodeURIComponent(token)}&token_type=${encodeURIComponent(type)}${nonceParam}`;
		},
	);
}
